import * as React from "react";
import { DefaultButton, IButtonStyles } from "@fluentui/react/lib/Button";
import { strings } from "@src/i18n/strings";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  padding: "1.5rem",
  backgroundColor: "#fff",
  borderRadius: "4px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  maxWidth: "520px",
  width: "90%",
};
const messageStyle: React.CSSProperties = {
  fontSize: "14px",
  color: "#323130",
  lineHeight: 1.5,
  margin: 0,
};
const buttonRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
  marginTop: "8px",
};
const thumbsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "8px",
  justifyContent: "flex-start",
};
const textareaStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "200px",
  borderRadius: "4px",
  border: "1px solid #edebe9",
  padding: "8px",
  fontSize: "13px",
  resize: "vertical",
  boxSizing: "border-box",
};

export interface FeedbackDialogProps {
  open: boolean;
  initialFeedback?: boolean | null;
  initialMessage?: string;
  submitting?: boolean;
  onSubmit: (feedback: boolean, message: string) => void | Promise<void>;
  onCancel: () => void;
}
export default function FeedbackDialog({
  open,
  initialFeedback = null,
  initialMessage = "",
  submitting = false,
  onSubmit,
  onCancel,
}: FeedbackDialogProps) {
  const feedbackStrings = strings.components.feedbackDialog;
  const [feedback, setFeedback] = React.useState<boolean | null>(initialFeedback);
  const [message, setMessage] = React.useState(initialMessage);

  React.useEffect(() => {
    if (open) {
      setFeedback(initialFeedback ?? null);
      setMessage(initialMessage ?? "");
    }
  }, [open, initialFeedback, initialMessage]);
  const handleOverlayClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && !submitting) onCancel();
    },
    [onCancel, submitting]
  );

  if (!open) return null;
  const canSubmit = feedback !== null && !submitting;
  const thumbsUpSelected = feedback === true;
  const thumbsDownSelected = feedback === false;
  const thumbsUpStyles: IButtonStyles = {
    root: {
      backgroundColor: thumbsUpSelected ? "#107C10" : "#f3f2f1",
      color: thumbsUpSelected ? "#ffffff" : "#107C10",
      borderColor: "#107C10",
    },
    rootHovered: {
      backgroundColor: thumbsUpSelected ? "#0B6A0B" : "#e1dfdd",
      color: "#ffffff",
      borderColor: "#0B6A0B",
    },
    icon: {
      color: thumbsUpSelected ? "#ffffff" : "#107C10",
    },
  };
  const thumbsDownStyles: IButtonStyles = {
    root: {
      backgroundColor: thumbsDownSelected ? "#A4262C" : "#f3f2f1",
      color: thumbsDownSelected ? "#ffffff" : "#A4262C",
      borderColor: "#A4262C",
    },
    rootHovered: {
      backgroundColor: thumbsDownSelected ? "#7F161D" : "#e1dfdd",
      color: "#ffffff",
      borderColor: "#7F161D",
    },
    icon: {
      color: thumbsDownSelected ? "#ffffff" : "#A4262C",
    },
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-dialog-title"
      style={overlayStyle}
      onClick={handleOverlayClick}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 id="feedback-dialog-title" style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>
          {feedbackStrings.title}
        </h2>
        <p style={messageStyle}>{feedbackStrings.helpText}</p>
        <div style={thumbsRowStyle}>
          <DefaultButton
            text={feedbackStrings.thumbsUp}
            iconProps={{ iconName: "Like" }}
            styles={thumbsUpStyles}
            onClick={() => setFeedback(true)}
          />
          <DefaultButton
            text={feedbackStrings.thumbsDown}
            iconProps={{ iconName: "Dislike" }}
            styles={thumbsDownStyles}
            onClick={() => setFeedback(false)}
          />
        </div>
        <div>
          <label
            style={{ display: "block", fontSize: "12px", marginBottom: "4px", color: "#605e5c" }}
          >
            {feedbackStrings.contextOptional}
          </label>
          <textarea
            style={textareaStyle}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={feedbackStrings.placeholder}
          />
        </div>
        <div style={buttonRowStyle}>
          <DefaultButton text={feedbackStrings.cancel} onClick={onCancel} disabled={submitting} />
          <DefaultButton
            text={submitting ? feedbackStrings.submitting : feedbackStrings.submitFeedback}
            primary
            disabled={!canSubmit}
            onClick={() => {
              if (feedback !== null) {
                void onSubmit(feedback, message.trim());
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
